import React from 'react';
import { Box, Skeleton, Text, Timeline } from '@mantine/core';

import { PriceChangeRequestHistoryItem } from '../../../codegen/graphql';

import { PriceChangeRequestHistoryTimelineItem } from './PriceChangeRequestHistoryTimelineItem';

type TimelineColor =
    | 'blue'
    | 'cyan'
    | 'dark'
    | 'gray'
    | 'green'
    | 'indigo'
    | 'lime'
    | 'orange'
    | 'pink'
    | 'primary'
    | 'red'
    | 'teal'
    | 'violet'
    | 'yellow';

export type PriceChangeRequestHistoryTimelineProps = {
    items: PriceChangeRequestHistoryItem[];
    timelineColor?: TimelineColor;
    activeIndex?: number;
    loading?: boolean;
    error?: Error | undefined;
};

export function PriceChangeRequestHistoryTimeline({
    items,
    timelineColor = 'primary',
    activeIndex,
    loading,
    error,
}: PriceChangeRequestHistoryTimelineProps) {
    if (loading) {
        return (
            <Box className="flex flex-col gap-5" px="xl" pb="xl" pt="md">
                {[...Array(3)].map((_, i) => (
                    <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`skeleton-${i}`}
                        className="flex gap-3 items-start"
                    >
                        <Skeleton height={24} width={24} circle />
                        <div className="flex flex-col gap-2 flex-1">
                            <Skeleton height={12} width="40%" />
                            <Skeleton height={10} width="70%" />
                            <Skeleton height={8} width={60} />
                        </div>
                    </div>
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Text size="sm" className="text-red-600" px="xl" py="md">
                Unable to load request history.
            </Text>
        );
    }

    const active =
        activeIndex !== undefined ? activeIndex : Math.max(0, items.length - 1);

    return (
        <Box px="xl" pb="xl" pt="md">
            <Timeline
                active={active}
                bulletSize={24}
                lineWidth={2}
                color={timelineColor}
            >
                {items.map((timelineItem: PriceChangeRequestHistoryItem) => (
                    <PriceChangeRequestHistoryTimelineItem
                        key={timelineItem.actionId}
                        timelineItem={timelineItem}
                    />
                ))}
            </Timeline>
        </Box>
    );
}
